import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { StyledModal } from '@shoflo/polymer/components/ErrorBoundary/LargeError.styled';
import { CameraSourceSettings } from '@shoflo/polymer/components/SourceSettings/CameraSourceSettings/CameraSourceSettings';
import setAudioInputDevice from '@shoflo/studio-redux/actions/meeting/setAudioInputDevice';
import setAudioOutputDevice from '@shoflo/studio-redux/actions/meeting/setAudioOutputDevice';
import setTransformedVideoInputs from '@shoflo/studio-redux/actions/meeting/setTransformedVideoInputs';
import setVideoInputDevice from '@shoflo/studio-redux/actions/meeting/setVideoInputDevice';
import uploadUserVirtualBackground from '@shoflo/studio-redux/actions/user/uploadUserVirtualBackground';
import deleteUserVirtualBackground from '@shoflo/studio-redux/actions/user/deleteUserVirtualBackground';

import knockAction from 'actions/Broadcast/knock';
import notifyDirectorAttendeeLeaving from 'actions/Broadcast/notifyDirectorAttendeeLeaving';
import { clearModal } from 'actions';
import useIsMobile from 'hooks/useIsMobile';
import { UPDATE_SETTINGS_MODAL } from 'constants/ModalTypes';
import { useGlobalUnload } from 'hooks/useWindowEvent';
import setUserBackground from '@shoflo/studio-redux/actions/backgroundFilters/setUserBackground';
import { RESET_USER_VIRTUAL_BACKGROUND } from '@studio/constants/VirtualBackground';
import { PASSED } from '@studio/constants/ReadinessCheck';
import updateSource from 'actions/source/updateSource';
import { shouldJoinWithoutKnock } from 'utils/broadcastPresenterHelper';
import { localAttendeeSource } from 'selectors/localAttendeeSource';

const SettingsModal = () => {
  const meeting = useSelector(state => state.meeting, shallowEqual);
  const modal = useSelector(state => state.modal);
  const knock = useSelector(state => state.knock);
  const isVideoActive = useSelector(state => state.isVideoActive);
  const user = useSelector(state => state.user, shallowEqual);
  const audioInputDevice = useSelector(state => state.audioInputDevice);
  const audioOutputDevice = useSelector(state => state.audioOutputDevice);
  const videoInputDevice = useSelector(state => state.videoInputDevice);
  const userBackground = useSelector(state => state.userBackground);
  const transformedVideoInputs = useSelector(
    state => state.transformedVideoInputs
  );
  const cameraStatus = useSelector(state => state.readinessCheck.camera);
  const localSource = useSelector(localAttendeeSource, shallowEqual);
  const joinWithoutKnockEnabled = shouldJoinWithoutKnock();

  const isCameraAvailable = cameraStatus === PASSED;

  const isMobile = useIsMobile();

  let isOpen = false;

  if (modal) {
    isOpen = modal.type === UPDATE_SETTINGS_MODAL;
  }

  const dispatch = useDispatch();

  const [presenterTitle, setPresenterTitle] = useState('');
  const [presenterDescription, setPresenterDescription] = useState('');

  const handlePageReloadOrClose = useCallback(() => {
    dispatch(notifyDirectorAttendeeLeaving({ closedOrReloadTab: true }, true));
  }, [dispatch]);

  const onCancel = async initialDataPayload => {
    const { transformedVideoInputs, videoInput } = initialDataPayload;

    dispatch(setVideoInputDevice(videoInput));
    dispatch(setTransformedVideoInputs(transformedVideoInputs));

    dispatch(clearModal(modal.type));
  };

  useGlobalUnload(handlePageReloadOrClose);
  useEffect(() => {
    if (knock) {
      setPresenterTitle(knock?.name);
      setPresenterDescription(knock?.description);
    }
  }, [knock]);

  useEffect(() => {
    if (localSource && joinWithoutKnockEnabled) {
      setPresenterTitle(localSource.title);
      setPresenterDescription(localSource.description);
    }
  }, [localSource, joinWithoutKnockEnabled]);

  const onSubmit = data => {
    const {
      audioInputDeviceId,
      audioOutputDeviceId,
      title,
      description,
      userBackground,
      transformedVideoInputs,
      videoInput
    } = data;

    dispatch(setAudioInputDevice(audioInputDeviceId));
    dispatch(setVideoInputDevice(videoInput));
    dispatch(setAudioOutputDevice(audioOutputDeviceId));
    dispatch(setUserBackground(userBackground));
    dispatch(setTransformedVideoInputs(transformedVideoInputs));

    if (!isVideoActive) {
      meeting.meetingSession.audioVideo.stopVideoInput();
    }

    if (localSource && joinWithoutKnockEnabled) {
      dispatch(updateSource(localSource._id, { title, description }));
    }

    if (!joinWithoutKnockEnabled) {
      dispatch(
        knockAction({
          presenterName: title,
          isTransient: false,
          presenterDescription: description || '',
          presenterDetailsUpdated: true,
          presenterPhoto: user?.photo
        })
      );
    }

    dispatch(clearModal(modal.type));
  };

  const style = { maxWidth: '800px' };

  const handleDeleteUserVirtualBackground = () => {
    dispatch(deleteUserVirtualBackground());
  };
  const handleUploadUserVirtualBackground = file =>
    dispatch(uploadUserVirtualBackground(file));
  const handleResetUserVirtualBackground = () =>
    dispatch({ type: RESET_USER_VIRTUAL_BACKGROUND });

  if (!isOpen) return null;

  return (
    <StyledModal
      isOpen={isOpen}
      fade={!isMobile}
      style={style}
      data-testId="speaker-settings"
    >
      <CameraSourceSettings
        meetingSession={meeting.meetingSession}
        title={presenterTitle}
        description={presenterDescription}
        onSubmit={onSubmit}
        onCancel={onCancel}
        audioInputDeviceId={audioInputDevice}
        audioOutputDeviceId={audioOutputDevice}
        videoInputDeviceId={videoInputDevice?.device ?? videoInputDevice}
        isCameraAvailable={isCameraAvailable}
        userBackground={userBackground}
        transformedVideoInputs={transformedVideoInputs}
        uploadUserVirtualBackground={handleUploadUserVirtualBackground}
        deleteUserVirtualBackground={handleDeleteUserVirtualBackground}
        resetUserVirtualBackground={handleResetUserVirtualBackground}
        user={user}
      />
    </StyledModal>
  );
};

export default SettingsModal;
